import designServices from '../../../../services/oneButtonServices/designServices';
import type { HubGridRow, ProductFinanceRow } from '../../../../@types/oneButtonType';

export type OpportunityProductSavePayload = {
    opportunity_Id: number;
    product_Type_Name: string;
    product_Type_Id: number;
    finance_Partner_Id: number;
    activeFlag: boolean;
    opportunity_Product_Id: number;
    finance_Partner: string;
    finance_Partner_Display_Name: string;
    finance_Method_Name: string;
};

export function hubRowToOpportunityProductPayload(
    r: HubGridRow | ProductFinanceRow,
    opportunityId: number | string,
    productTypeList: { Text: string; Value?: string | number }[] = [],
): OpportunityProductSavePayload {
    const hasValidPartner =
        !!r.finance_Partner_Display_Name && r.finance_Partner_Display_Name !== 'Select Finance Partner';
    const partner = hasValidPartner ? String(r.finance_Partner_Display_Name) : '';

    return {
        opportunity_Id: +opportunityId,
        product_Type_Name: r.product_Type_Name,
        product_Type_Id:
            r.product_Type_Id || Number(productTypeList.find((x) => x.Text === r.product_Type_Name)?.Value) || 0,
        finance_Partner_Id: partner ? r.finance_Partner_Id : 0,
        activeFlag: !!r.activeFlag,
        opportunity_Product_Id: r.opportunity_Product_Id || 0,
        finance_Partner: partner ? r.finance_Partner || partner : '',
        finance_Partner_Display_Name: partner,
        finance_Method_Name: r.finance_Method_Name === 'Select Method' ? '' : r.finance_Method_Name || '',
    };
}

/** Build API payload; scoped save keeps persisted values for non-target products. */
export function buildOpportunityProductSavePayload(
    rows: HubGridRow[],
    originalArr: unknown[],
    opportunityId: number | string,
    productTypeList: { Text: string; Value?: string | number }[],
    scopeProductName?: string,
): OpportunityProductSavePayload[] {
    const savedList = Array.isArray(originalArr) ? originalArr : [];

    const shouldInclude = (r: HubGridRow) => {
        if (scopeProductName) {
            if (r.product_Type_Name === scopeProductName) return true;
            return savedList.some((op) => matchProductName(op, r.product_Type_Name));
        }
        return r.activeFlag || savedList.some((op) => matchProductName(op, r.product_Type_Name));
    };

    return rows.filter(shouldInclude).map((r) => {
        if (scopeProductName && r.product_Type_Name !== scopeProductName) {
            const saved = savedList.find((op) => matchProductName(op, r.product_Type_Name));
            if (saved) {
                const fromSaved = rowFromApi(saved, r.product_Type_Name);
                const activeFlag =
                    (saved as { ActiveFlag?: boolean }).ActiveFlag !== false &&
                    (saved as { activeFlag?: boolean }).activeFlag !== false;
                return hubRowToOpportunityProductPayload(
                    { ...fromSaved, activeFlag },
                    opportunityId,
                    productTypeList,
                );
            }
            return hubRowToOpportunityProductPayload({ ...r, activeFlag: false }, opportunityId, productTypeList);
        }

        // Storage methods require Battery. Do not persist Solar "PPA Storage" (etc.) when Battery is off.
        if (r.product_Type_Name === 'Solar' && isStorageFinanceMethod(r.finance_Method_Name)) {
            const batteryActive = rows.some((x) => x.product_Type_Name === 'Battery' && x.activeFlag);
            if (!batteryActive) {
                return hubRowToOpportunityProductPayload(
                    { ...r, finance_Method_Name: stripStorageFromFinanceMethod(r.finance_Method_Name) },
                    opportunityId,
                    productTypeList,
                );
            }
        }

        return hubRowToOpportunityProductPayload(r, opportunityId, productTypeList);
    });
}

export function validateProductFinanceForSave(
    rows: HubGridRow[],
    scopeProductName?: string,
): string | null {
    if (scopeProductName) {
        const row = rows.find((r) => r.product_Type_Name === scopeProductName);
        if (!row) return 'Product not found.';
        if (!row.activeFlag) return `Enable ${scopeProductName} before saving.`;
        if (!row.finance_Partner_Display_Name || row.finance_Partner_Display_Name === 'Select Finance Partner') {
            return `Select a finance partner for ${scopeProductName}.`;
        }
        return null;
    }

    const activeRows = rows.filter((r) => r.activeFlag);
    if (!activeRows.length) return 'Please select at least one product.';

    const missingPartners = activeRows.filter(
        (r) => !r.finance_Partner_Display_Name || r.finance_Partner_Display_Name === 'Select Finance Partner',
    );
    if (missingPartners.length) {
        return `Select a finance partner for: ${missingPartners.map((r) => r.product_Type_Name).join(', ')}`;
    }
    return null;
}

export const PRODUCT_DISPLAY_ORDER = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];

export const isRoofFamily = (product: string) => product === 'Roof' || product === 'RR' || product === 'RRR';

export const isLightreach = (p: string) => /light\s*reach/i.test(p);
export const isSunnova = (p: string) => /sunnova/i.test(p);
export const isGoodLeap = (p: string) => /good\s*leap/i.test(p);
export const isEnfin = (p: string) => /enfin/i.test(p);
export const isIgs = (p: string) => String(p || '').trim().replace(/\s+/g, '').toLowerCase() === 'igs';

/** Battery Only / Roof Only collapse to the same product key as Battery / Roof. */
export function normalizeFinanceLeadProductKey(type: string | null | undefined): string {
    return String(type || '')
        .trim()
        .toLowerCase()
        .replace(/\s+only$/, '');
}

/** True when a lead's product type matches the Partner Sync product that opened the list. */
export function leadTypeMatchesSelectedProduct(
    leadType: string | null | undefined,
    productName: string | null | undefined,
): boolean {
    const selected = normalizeFinanceLeadProductKey(productName);
    if (!selected) return true;
    const lead = normalizeFinanceLeadProductKey(leadType);
    if (!lead) return false;
    return lead === selected || lead.includes(selected) || selected.includes(lead);
}

/**
 * Solar finance methods that include storage / battery bundling.
 * These require Battery in the quote for every partner that offers them
 * (Good Leap, Sunnova, Lightreach, Enfin, …).
 * Not included: Battery Loan, Battery Lease (battery-only methods).
 */
export const SOLAR_STORAGE_FINANCE_METHODS = ['Lease Storage', 'Loan Storage', 'PPA Storage'] as const;

export type SolarStorageFinanceMethod = (typeof SOLAR_STORAGE_FINANCE_METHODS)[number];

function normalizeFinanceMethodKey(method: string | null | undefined): string {
    return String(method || '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ' ');
}

/** True for Lease Storage / Loan Storage / PPA Storage (any partner). */
export function isStorageFinanceMethod(method: string | null | undefined): boolean {
    const key = normalizeFinanceMethodKey(method);
    if (!key) return false;
    return SOLAR_STORAGE_FINANCE_METHODS.some((m) => normalizeFinanceMethodKey(m) === key);
}

/**
 * Storage methods require Battery. Map e.g. "PPA Storage" → "PPA",
 * "Lease Storage" → "Lease", "Loan Storage" → "Loan".
 */
export function stripStorageFromFinanceMethod(method: string | null | undefined): string {
    const m = String(method || '').trim();
    if (!m || !isStorageFinanceMethod(m)) return m;
    const stripped = m.replace(/\s+storage$/i, '').trim();
    return stripped || m;
}

/**
 * Block Solar-only quote creation when Products has a Storage method.
 * Applies to all partners — Storage always requires Battery in the quote.
 */
export function solarStorageRequiresBatteryInQuote(
    includeSolar: boolean,
    includeBattery: boolean,
    solarFinanceMethod: string | null | undefined,
): boolean {
    return !!(includeSolar && !includeBattery && isStorageFinanceMethod(solarFinanceMethod));
}

/** Battery-only LR methods — not comparable to Solar Lease / PPA for SB single-lead rules. */
export function isLightreachBatteryOnlyFinanceMethod(method: string | null | undefined): boolean {
    const key = normalizeFinanceMethodKey(method);
    if (!key) return false;
    return key === 'battery lease' || key.startsWith('battery lease') || key === 'battery loan';
}

/** PPA vs Lease family for Lightreach SB single-quote rules (not storage methods). */
export function lightreachQuoteMethodFamily(method: string | null | undefined): 'ppa' | 'lease' | null {
    const key = normalizeFinanceMethodKey(method);
    if (!key || isStorageFinanceMethod(method) || isLightreachBatteryOnlyFinanceMethod(method)) return null;
    if (key === 'ppa' || key.startsWith('ppa ') || key.includes('ppa ez') || key === 'prepaid ppa ez') {
        return 'ppa';
    }
    if (key === 'lease' || key.startsWith('lease ')) {
        return 'lease';
    }
    if (key.includes('loan')) {
        return 'lease';
    }
    return null;
}

/**
 * Lightreach Solar + Battery on one opportunity: force a single combined quote/contract only when
 * both active products use the same method family (PPA with PPA, Lease with Lease).
 * e.g. Solar PPA + Battery Battery Lease → separate quotes allowed.
 */
export function lightreachSolarBatteryRequireSingleQuote(
    solarMethod: string | null | undefined,
    batteryMethod: string | null | undefined,
): boolean {
    const solarFamily = lightreachQuoteMethodFamily(solarMethod);
    const batteryFamily = lightreachQuoteMethodFamily(batteryMethod);
    if (!solarFamily || !batteryFamily) return false;
    return solarFamily === batteryFamily;
}

function activeHubRowPartnerDisplay(row: HubGridRow | ProductFinanceRow): string {
    return String(row.finance_Partner_Display_Name ?? row.finance_Partner ?? '').trim();
}

function activeHubRowMethod(row: HubGridRow | ProductFinanceRow): string {
    return String(row.finance_Method_Name ?? '').trim();
}

/** Products hub rows: LR Solar + LR Battery share one lead when method families match (PPA↔PPA, Lease↔Lease). */
export function lightreachSolarBatteryRequireSingleLeadFromHubRows(rows: HubGridRow[]): boolean {
    const solar = rows.find((r) => r.product_Type_Name === 'Solar' && r.activeFlag);
    const battery = rows.find((r) => r.product_Type_Name === 'Battery' && r.activeFlag);
    if (!solar || !battery) return false;
    if (!isLightreach(activeHubRowPartnerDisplay(solar)) || !isLightreach(activeHubRowPartnerDisplay(battery))) {
        return false;
    }
    return lightreachSolarBatteryRequireSingleQuote(activeHubRowMethod(solar), activeHubRowMethod(battery));
}

/** Hide Battery row Partner Sync — use Solar sync / single S lead when SB share one LR lead. */
export function hideBatteryLightreachPartnerSync(rows: HubGridRow[]): boolean {
    return lightreachSolarBatteryRequireSingleLeadFromHubRows(rows);
}

function opportunityProductTypeName(p: any): string {
    return String(p?.Product_Type_Name ?? p?.product_Type_Name ?? p?.ProductTypeName ?? '').trim();
}

function opportunityProductPartner(p: any): string {
    return String(
        p?.Finance_Partner_Display_Name ?? p?.FinancePartnerDisplayName ?? p?.Finance_Partner ?? '',
    ).trim();
}

function opportunityProductMethod(p: any): string {
    return String(p?.Finance_Method_Name ?? p?.finance_Method_Name ?? '').trim();
}

function isActiveOpportunityProduct(p: any): boolean {
    return p?.ActiveFlag !== false && p?.activeFlag !== false;
}

/** Persisted opportunity products: same single-lead rule as hub rows. */
/**
 * V1 product-section: Lightreach Battery methods = API list, plus Solar LR finance method(s) when Solar is also LR,
 * always including Battery Lease (capability permitting). Otherwise default to Battery Lease only.
 */
export function resolveLightreachBatteryMethodOptions(
    apiMethods: string[],
    allRows: HubGridRow[],
    allowBatteryLease: boolean,
): string[] {
    const dedupe = (items: string[]) => {
        const seen = new Set<string>();
        return items
            .map((m) => String(m || '').trim())
            .filter((m) => {
                if (!m) return false;
                const key = normalizeFinanceMethodKey(m);
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    };

    let methods = dedupe(Array.isArray(apiMethods) ? apiMethods : []);
    const solar = allRows.find((r) => r.product_Type_Name === 'Solar' && r.activeFlag);
    const solarIsLightreach =
        !!solar && isLightreach(String(solar.finance_Partner_Display_Name ?? solar.finance_Partner ?? ''));

    const appendBatteryLease = () => {
        if (!allowBatteryLease) return;
        if (!methods.some((m) => normalizeFinanceMethodKey(m) === 'battery lease')) {
            methods.push('Battery Lease');
        }
    };

    if (solarIsLightreach) {
        const fromSolarOptions = (solar?.methodOptions ?? []).filter(
            (m) => !isLightreachBatteryOnlyFinanceMethod(m),
        );
        const solarSelected = String(solar?.finance_Method_Name ?? '').trim();
        const extras: string[] = [...fromSolarOptions];
        if (solarSelected && !isLightreachBatteryOnlyFinanceMethod(solarSelected)) {
            extras.unshift(solarSelected);
        }
        methods = dedupe([...extras, ...methods]);
        appendBatteryLease();
    } else if (methods.length === 0 && allowBatteryLease) {
        methods = ['Battery Lease'];
    } else {
        appendBatteryLease();
    }

    if (!allowBatteryLease) {
        methods = methods.filter((m) => normalizeFinanceMethodKey(m) !== 'battery lease');
    }

    return methods;
}

export function lightreachSolarBatteryRequireSingleLeadFromOpportunityProducts(products: any[]): boolean {
    const list = Array.isArray(products) ? products : [];
    const solar = list.find((p) => isActiveOpportunityProduct(p) && opportunityProductTypeName(p) === 'Solar');
    const battery = list.find((p) => isActiveOpportunityProduct(p) && opportunityProductTypeName(p) === 'Battery');
    if (!solar || !battery) return false;
    if (!isLightreach(opportunityProductPartner(solar)) || !isLightreach(opportunityProductPartner(battery))) {
        return false;
    }
    return lightreachSolarBatteryRequireSingleQuote(
        opportunityProductMethod(solar),
        opportunityProductMethod(battery),
    );
}

export function roofProductCodeForC4(product: string): string {
    if (product === 'RR') return 'RR';
    if (product === 'RRR') return 'RRR';
    return 'R';
}

export function partnerKeyForSyncModal(display: string): string {
    const x = String(display || '').toLowerCase();
    if (x.includes('light')) return 'LightReach';
    if (x.includes('good') && x.includes('leap')) return 'GoodLeap';
    if (x.includes('igs')) return 'IGS';
    if (x.includes('sunnova')) return 'IGS';
    if (x.includes('service')) return 'GoodLeap';
    if (x.includes('cash')) return 'Cash';
    if(x.includes('enfin')) return 'Enfin';
    return 'GoodLeap';
}

export function rowFromApi(p: any | undefined, productType: string): ProductFinanceRow {
    if (!p) {
        return {
            product_Type_Name: productType,
            product_Type_Id: 0,
            opportunity_Product_Id: 0,
            finance_Partner_Id: 0,
            finance_Partner: '',
            finance_Partner_Display_Name: '',
            finance_Method_Name: '',
            activeFlag: false,
        };
    }
    return {
        product_Type_Name: String(p.Product_Type_Name || p.product_Type_Name || productType),
        product_Type_Id: Number(p.Product_Type_Id ?? p.product_Type_Id ?? 0) || 0,
        opportunity_Product_Id: Number(p.Opportunity_Product_Id ?? p.opportunity_Product_Id ?? 0) || 0,
        finance_Partner_Id: Number(p.Finance_Partner_Id ?? p.FinancePartnerId ?? p.finance_Partner_Id ?? 0) || 0,
        finance_Partner: String(p.Finance_Partner ?? p.finance_Partner ?? ''),
        finance_Partner_Display_Name: String(
            p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.finance_Partner_Display_Name ?? ''
        ),
        finance_Method_Name: String(p.Finance_Method_Name ?? p.FinanceMethod ?? p.finance_Method_Name ?? ''),
        activeFlag: p.ActiveFlag !== false && p.activeFlag !== false,
    };
}

export function matchProductName(p: any, target: string): boolean {
    const n = String(p?.Product_Type_Name ?? p?.ProductType ?? p?.product_Type_Name ?? '').trim();
    if (n.toLowerCase() === target.toLowerCase()) return true;
    if (target === 'RR' && /^(rr|roof\s*replacement)$/i.test(n)) return true;
    if (target === 'RRR' && /rrr/i.test(n)) return true;
    return false;
}

/** Keep store partner/method in sync with the just-saved hub payload if GetOpportunityDetail is stale. */
export function mergeSavedProductsIntoOpportunityDetails(
    details: Record<string, unknown> | null | undefined,
    savedPayload: OpportunityProductSavePayload[],
): Record<string, unknown> | null | undefined {
    if (!details || !Array.isArray(savedPayload) || savedPayload.length === 0) {
        return details;
    }
    const current = details.OpportunityProduct ?? details.opportunityProduct ?? [];
    const list = Array.isArray(current)
        ? current.map((row) => (row && typeof row === 'object' ? { ...row } : row))
        : [];
    for (const saved of savedPayload) {
        const name = String(saved.product_Type_Name ?? '').trim();
        if (!name) continue;
        const patch = {
            Product_Type_Name: name,
            product_Type_Name: name,
            Finance_Partner_Display_Name: saved.finance_Partner_Display_Name,
            finance_Partner_Display_Name: saved.finance_Partner_Display_Name,
            Finance_Partner: saved.finance_Partner,
            finance_Partner: saved.finance_Partner,
            Finance_Method_Name: saved.finance_Method_Name,
            finance_Method_Name: saved.finance_Method_Name,
            Finance_Partner_Id: saved.finance_Partner_Id,
            finance_Partner_Id: saved.finance_Partner_Id,
            ActiveFlag: saved.activeFlag,
            activeFlag: saved.activeFlag,
        };
        const idx = list.findIndex((row) => matchProductName(row, name));
        if (idx >= 0) {
            list[idx] = { ...(list[idx] as object), ...patch };
        } else {
            list.push(patch);
        }
    }
    return {
        ...details,
        OpportunityProduct: list,
        opportunityProduct: list,
    };
}

/** Positive Int32 FK only — 0/null is invalid on FinancePartnerQuoteAdders. */
export function positiveProductTypeId(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * V1 generate-quote getProductTypeAdders: ProductTypeID comes from GetProductType
 * (Product_Type_Name == adder.ProductName), not from `/api/Contract/Adders`.
 * V2 master list uses Text/Value; opportunity products carry Product_Type_Id.
 */
export function resolveProductTypeIdFromCategory(
    productName: string | null | undefined,
    productTypeList: { Text?: string; Value?: string | number; Product_Type_Name?: string; Product_Type_Id?: number }[] = [],
    opportunityProducts: unknown[] = [],
    fromAdder?: unknown,
): number {
    const fromApi = positiveProductTypeId(fromAdder);
    if (fromApi > 0) return fromApi;

    const name = String(productName || '').trim().toLowerCase();
    if (!name) return 0;

    const fromList = productTypeList.find(
        (ele) => String(ele.Text ?? ele.Product_Type_Name ?? '').trim().toLowerCase() === name,
    );
    const listId = positiveProductTypeId(fromList?.Value ?? fromList?.Product_Type_Id);
    if (listId > 0) return listId;

    const fromOpp = (Array.isArray(opportunityProducts) ? opportunityProducts : []).find((p: any) =>
        matchProductName(p, productName || ''),
    );
    return positiveProductTypeId(
        (fromOpp as any)?.Product_Type_Id ?? (fromOpp as any)?.product_Type_Id,
    );
}

const FINANCE_PLACEHOLDER_RE = /^(select\s+(finance\s+)?partner|select\s+finance|select\s+method)$/i;

function normalizeHubPartnerName(name: string): string {
    const n = name.trim().toLowerCase();
    if (!n || n.includes('select finance')) return '';
    return n;
}

function normalizeHubMethodName(name: string): string {
    const n = name.trim().toLowerCase();
    if (!n || n === 'select method') return '';
    return n;
}

function savedPartnerName(p: any): string {
    return normalizeHubPartnerName(
        String(p?.Finance_Partner_Display_Name ?? p?.FinancePartnerDisplayName ?? p?.Finance_Partner ?? ''),
    );
}

/** True when hub row differs from persisted opportunity product (partner, method, or active flag). */
export function hubRowHasUnsavedChanges(row: HubGridRow, savedArr: unknown[]): boolean {
    const savedList = Array.isArray(savedArr) ? savedArr : [];
    const saved = savedList.find((p: any) => matchProductName(p, row.product_Type_Name));

    const rowActive = !!row.activeFlag;
    const savedActive = !!(saved?.ActiveFlag ?? saved?.activeFlag ?? false);
    if (rowActive !== savedActive) return true;

    if (!rowActive) return false;

    const rowPartner = normalizeHubPartnerName(String(row.finance_Partner_Display_Name ?? ''));
    if (rowPartner !== savedPartnerName(saved)) return true;

    const rowMethod = normalizeHubMethodName(String(row.finance_Method_Name ?? ''));
    const savedMethod = normalizeHubMethodName(
        String(saved?.Finance_Method_Name ?? saved?.FinanceMethod ?? ''),
    );
    if (rowMethod !== savedMethod) return true;

    return false;
}

/** True when Solar finance partner display name differs from persisted opportunity product. */
export function solarFinancePartnerChanged(rows: HubGridRow[], savedArr: unknown[]): boolean {
    const solarRow = rows.find((r) => r.product_Type_Name === 'Solar');
    if (!solarRow) return false;

    const savedList = Array.isArray(savedArr) ? savedArr : [];
    const saved = savedList.find((p: any) => matchProductName(p, 'Solar'));

    const rowPartner = normalizeHubPartnerName(String(solarRow.finance_Partner_Display_Name ?? ''));
    return rowPartner !== savedPartnerName(saved);
}

/** True when a non-placeholder finance partner is persisted for this product (hub matches saved). */
export function hubRowHasSavedFinancePartner(row: HubGridRow | undefined, savedArr: unknown[]): boolean {
    if (!row) return false;

    const displayName = String(row.finance_Partner_Display_Name ?? '').trim();
    if (!displayName || FINANCE_PLACEHOLDER_RE.test(displayName)) return false;

    const savedList = Array.isArray(savedArr) ? savedArr : [];
    const saved = savedList.find((p: any) => matchProductName(p, row.product_Type_Name));
    const persistedPartner = savedPartnerName(saved);
    if (!persistedPartner) return false;

    return !hubRowHasUnsavedChanges(row, savedArr);
}

/** Stable key for persisted opportunity products so the hub can rehydrate after save. */
export function savedOpportunityProductsFingerprint(savedArr: unknown[]): string {
    const list = Array.isArray(savedArr) ? savedArr : [];
    return list
        .map((p: any) =>
            [
                String(p?.Product_Type_Name ?? p?.product_Type_Name ?? '').trim().toLowerCase(),
                String(
                    p?.Finance_Partner_Display_Name ??
                        p?.FinancePartnerDisplayName ??
                        p?.Finance_Partner ??
                        p?.finance_Partner ??
                        '',
                )
                    .trim()
                    .toLowerCase(),
                String(p?.Finance_Method_Name ?? p?.FinanceMethod ?? p?.finance_Method_Name ?? '')
                    .trim()
                    .toLowerCase(),
                String(p?.ActiveFlag ?? p?.activeFlag ?? ''),
                String(p?.Opportunity_Product_Id ?? p?.opportunity_Product_Id ?? ''),
            ].join(':'),
        )
        .sort()
        .join('|');
}

/** True when hub rows have no persisted product id or partner yet (safe to replace with saved data). */
export function hubRowsLookLikePlaceholders(rows: HubGridRow[]): boolean {
    if (!rows.length) return true;
    return rows.every((row) => {
        const partner = String(row.finance_Partner_Display_Name ?? '').trim();
        const isPlaceholder = !partner || FINANCE_PLACEHOLDER_RE.test(partner);
        return isPlaceholder && !(Number(row.opportunity_Product_Id) > 0);
    });
}
